import React, { useState, useEffect } from 'react';
import { X, Shield, Cookie, Settings, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';

interface CookieConsentModalProps {
  onAccept: (preferences: CookiePreferences) => void;
  onReject: () => void;
}

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
}

const CookieConsentModal: React.FC<CookieConsentModalProps> = ({ onAccept, onReject }) => {
  const { t } = useTranslation();
  const { tenant } = useTenant();
  const { user } = useAuth();
  const [isVisible, setIsVisible] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true, // Zorunlu çerezler her zaman aktif
    analytics: false,
    marketing: false
  });
  const [showDetails, setShowDetails] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    console.log('CookieConsentModal - user:', user);
    console.log('CookieConsentModal - tenant:', tenant);
    
    // Admin kullanıcıları için çerez onayı gösterme (tenant kontrolü olmadan)
    if (user?.email === 'admin@lotusrecruiter.com') {
      console.log('Admin user detected, hiding cookie consent modal');
      return;
    }
    
    // Tenant bazlı çerez onayı kontrolü
    if (!tenant) {
      console.log('No tenant found, hiding modal');
      return; // Tenant yoksa gösterme
    }
    
    const consentKey = `cookie-consent-${tenant.id}`;
    const consent = localStorage.getItem(consentKey);
    console.log('Cookie consent key:', consentKey);
    console.log('Cookie consent value:', consent);
    
    if (!consent) {
      console.log('No consent found, showing modal');
      setIsVisible(true);
    } else {
      console.log('Consent already given, hiding modal');
    }
  }, [tenant, user]);

  const handlePreferenceChange = (type: keyof CookiePreferences) => {
    if (type === 'essential') return; // Zorunlu çerezler değiştirilemez
    setPreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const handleAcceptAll = async () => {
    if (!tenant) return;
    
    setLoading(true);
    try {
      // Tüm çerezleri kabul et
      const allPreferences: CookiePreferences = {
        essential: true,
        analytics: true,
        marketing: true
      };

      // Backend'e bildir
      await recordCookieConsent('all', allPreferences);

      // Tenant bazlı LocalStorage'a kaydet
      const consentKey = `cookie-consent-${tenant.id}`;
      localStorage.setItem(consentKey, 'accepted');
      localStorage.setItem(`cookie-preferences-${tenant.id}`, JSON.stringify(allPreferences));
      localStorage.setItem(`cookie-consent-date-${tenant.id}`, new Date().toISOString());

      onAccept(allPreferences);
      setIsVisible(false);
    } catch (error) {
      console.error('Cookie consent recording failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptSelected = async () => {
    if (!tenant) return;
    
    setLoading(true);
    try {
      // Seçili çerezleri kabul et
      await recordCookieConsent('selected', preferences);

      // Tenant bazlı LocalStorage'a kaydet
      const consentKey = `cookie-consent-${tenant.id}`;
      localStorage.setItem(consentKey, 'selected');
      localStorage.setItem(`cookie-preferences-${tenant.id}`, JSON.stringify(preferences));
      localStorage.setItem(`cookie-consent-date-${tenant.id}`, new Date().toISOString());

      onAccept(preferences);
      setIsVisible(false);
    } catch (error) {
      console.error('Cookie consent recording failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!tenant) return;
    
    setLoading(true);
    try {
      // Sadece zorunlu çerezleri kabul et
      const essentialOnly: CookiePreferences = {
        essential: true,
        analytics: false,
        marketing: false
      };

      await recordCookieConsent('essential', essentialOnly);

      // Tenant bazlı LocalStorage'a kaydet
      const consentKey = `cookie-consent-${tenant.id}`;
      localStorage.setItem(consentKey, 'rejected');
      localStorage.setItem(`cookie-preferences-${tenant.id}`, JSON.stringify(essentialOnly));
      localStorage.setItem(`cookie-consent-date-${tenant.id}`, new Date().toISOString());

      onReject();
      setIsVisible(false);
    } catch (error) {
      console.error('Cookie consent recording failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const recordCookieConsent = async (consentType: string, prefs: CookiePreferences) => {
    if (!tenant) return;
    
    // Her çerez türü için ayrı kayıt
    const promises = Object.entries(prefs).map(([type, accepted]) => 
      api.post('/api/kvkk/cookie-consent', {
        tenant_id: tenant.id, // Tenant ID'yi backend'e gönder
        consent_type: type,
        is_accepted: accepted,
        consent_metadata: {
          consent_type: consentType,
          timestamp: new Date().toISOString(),
          source: 'modal',
          tenant_name: tenant.name,
          tenant_slug: tenant.slug
        }
      })
    );

    await Promise.all(promises);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Cookie className="text-lotus-gold" size={24} />
            <h3 className="text-lg font-semibold text-gray-900">
              {t('cookieConsent.title')}
            </h3>
          </div>
          <button
            onClick={handleReject}
            className="text-gray-400 hover:text-gray-600"
            title={t('cookieConsent.closeTitle')}
          >
            <X size={20} />
          </button>
        </div>

        {/* Description */}
        <div className="text-sm text-gray-600 mb-6">
          <p className="mb-4">
            {t('cookieConsent.description')}
          </p>
          
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center gap-2 text-lotus-gold hover:text-lotus-gold/80 text-sm font-medium"
          >
            <Settings size={16} />
            {showDetails ? t('cookieConsent.hideDetails') : t('cookieConsent.showDetails')}
          </button>
        </div>

        {/* Detailed Preferences */}
        {showDetails && (
          <div className="space-y-3 mb-6">
            {/* Essential Cookies */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Shield className="text-gray-600" size={16} />
                  <span className="text-sm font-medium text-gray-900">{t('cookieConsent.essentialTitle')}</span>
                </div>
                <div className="w-4 h-4 bg-green-500 rounded flex items-center justify-center">
                  <Check size={12} className="text-white" />
                </div>
              </div>
              <p className="text-xs text-gray-600">
                {t('cookieConsent.essentialDescription')}
              </p>
            </div>

            {/* Analytics Cookies */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Settings className="text-blue-600" size={16} />
                  <span className="text-sm font-medium text-blue-900">{t('cookieConsent.analyticsTitle')}</span>
                </div>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={() => handlePreferenceChange('analytics')}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                </label>
              </div>
              <p className="text-xs text-gray-600">
                {t('cookieConsent.analyticsDescription')}
              </p>
            </div>

            {/* Marketing Cookies */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Cookie className="text-purple-600" size={16} />
                  <span className="text-sm font-medium text-purple-900">{t('cookieConsent.marketingTitle')}</span>
                </div>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={() => handlePreferenceChange('marketing')}
                    className="w-4 h-4 text-purple-600 rounded"
                  />
                </label>
              </div>
              <p className="text-xs text-gray-600">
                {t('cookieConsent.marketingDescription')}
              </p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={handleAcceptAll}
            disabled={loading}
            className="w-full bg-lotus-gold text-white px-4 py-2 rounded-lg hover:bg-lotus-gold/90 transition-colors disabled:opacity-50 font-medium text-sm"
          >
            {loading ? t('common.processing') : t('cookieConsent.acceptAll')}
          </button>
          
          {showDetails && (
            <button
              onClick={handleAcceptSelected}
              disabled={loading}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 font-medium text-sm"
            >
              {loading ? t('common.processing') : t('cookieConsent.acceptSelected')}
            </button>
          )}
          
          <button
            onClick={handleReject}
            disabled={loading}
            className="w-full bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 font-medium text-sm"
          >
            {loading ? t('common.processing') : t('cookieConsent.acceptEssential')}
          </button>
        </div>

        {/* Footer */}
        <div className="text-xs text-gray-500 mt-4">
          <a 
            href="/legal/gizlilik-politikasi#cerler" 
            target="_blank" 
            rel="noopener noreferrer"
            className="underline hover:text-gray-700"
          >
            {t('cookieConsent.policyLink')}
          </a>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentModal;
